import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {
  DEFAULT_MAX_RANGE_DAYS,
  DEFAULT_YEARS_BACK,
  DatePartsValue,
  clampDateParts,
  dateFromParts,
  getDayOptionsForParts,
  getMonthOptionsForParts,
  getYearOptions,
  isDatePartsComplete,
  startOfDay,
  validateDateRangeSelection,
} from './attendanceDateRangeUtils'

export {formatDateDdMmYyyy, parseDdMmYyyy} from './attendanceDateRangeUtils'

type ChatbotDateRangeInputProps = {
  onDateRangeSelect: (start: Date, end: Date) => void
  maxDate?: Date
  minDate?: Date
  maxRangeDays?: number
  yearsBack?: number
  theme?: 'light' | 'dark'
}

const EMPTY_PARTS: DatePartsValue = {day: null, month: null, year: null}

type CompactDateRowProps = {
  label: string
  labelId: string
  parts: DatePartsValue
  onChange: (next: DatePartsValue) => void
  minBound: Date
  maxBound: Date
  selectStyle: React.CSSProperties
  yearSelectStyle: React.CSSProperties
  optionStyle: React.CSSProperties
}

const CompactDateRow: React.FC<CompactDateRowProps> = ({
  label,
  labelId,
  parts,
  onChange,
  minBound,
  maxBound,
  selectStyle,
  yearSelectStyle,
  optionStyle,
}) => {
  const intl = useIntl()

  const years = useMemo(() => getYearOptions(minBound, maxBound), [maxBound, minBound])

  const months = useMemo(
    () => getMonthOptionsForParts(parts, minBound, maxBound),
    [maxBound, minBound, parts]
  )

  const days = useMemo(
    () => getDayOptionsForParts(parts, minBound, maxBound),
    [maxBound, minBound, parts]
  )

  const dayLabel = intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_DAY'})
  const monthLabel = intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH'})
  const yearLabel = intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_YEAR'})

  return (
    <div
      className='d-flex align-items-center gap-1'
      style={{flex: '1 1 auto', minWidth: 0}}
      role='group'
      aria-label={intl.formatMessage({id: labelId})}
    >
      <span
        style={{
          fontSize: '11px',
          fontWeight: 600,
          minWidth: 32,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <select
        aria-label={`${label} ${dayLabel}`}
        value={parts.day ?? ''}
        onChange={(e) => {
          const day = e.target.value === '' ? null : Number(e.target.value)
          onChange({...parts, day})
        }}
        style={selectStyle}
      >
        <option value='' style={optionStyle}>
          DD
        </option>
        {days.map((d) => (
          <option key={d} value={d} style={optionStyle}>
            {String(d).padStart(2, '0')}
          </option>
        ))}
      </select>
      <select
        aria-label={`${label} ${monthLabel}`}
        value={parts.month ?? ''}
        onChange={(e) => {
          const month = e.target.value === '' ? null : Number(e.target.value)
          onChange({...parts, month})
        }}
        style={selectStyle}
      >
        <option value='' style={optionStyle}>
          MM
        </option>
        {months.map((m) => (
          <option key={m} value={m} style={optionStyle}>
            {String(m).padStart(2, '0')}
          </option>
        ))}
      </select>
      <select
        aria-label={`${label} ${yearLabel}`}
        value={parts.year ?? ''}
        onChange={(e) => {
          const year = e.target.value === '' ? null : Number(e.target.value)
          onChange({...parts, year})
        }}
        style={yearSelectStyle}
      >
        <option value='' style={optionStyle}>
          YYYY
        </option>
        {years.map((y) => (
          <option key={y} value={y} style={optionStyle}>
            {y}
          </option>
        ))}
      </select>
    </div>
  )
}

const ChatbotDateRangeInput: React.FC<ChatbotDateRangeInputProps> = ({
  onDateRangeSelect,
  maxDate,
  minDate,
  maxRangeDays = DEFAULT_MAX_RANGE_DAYS,
  yearsBack = DEFAULT_YEARS_BACK,
  theme = 'light',
}) => {
  const intl = useIntl()
  const [startParts, setStartParts] = useState<DatePartsValue>(EMPTY_PARTS)
  const [endParts, setEndParts] = useState<DatePartsValue>(EMPTY_PARTS)

  const effectiveMaxDate = useMemo(() => startOfDay(maxDate ?? new Date()), [maxDate])

  const effectiveMinDate = useMemo(() => {
    if (minDate) return startOfDay(minDate)
    const d = new Date(effectiveMaxDate)
    d.setFullYear(d.getFullYear() - yearsBack)
    return startOfDay(d)
  }, [effectiveMaxDate, minDate, yearsBack])

  const startBounds = useMemo(
    () => ({min: effectiveMinDate, max: effectiveMaxDate}),
    [effectiveMinDate, effectiveMaxDate]
  )

  const endBounds = useMemo(() => {
    const start = dateFromParts(startParts)
    let min = effectiveMinDate
    if (start && start > min) {
      min = start
    }

    let max = effectiveMaxDate
    if (start && maxRangeDays > 0) {
      const rangeCap = new Date(start)
      rangeCap.setDate(rangeCap.getDate() + maxRangeDays)
      const capped = startOfDay(rangeCap)
      if (capped < max) {
        max = capped
      }
    }

    return {min, max}
  }, [effectiveMinDate, effectiveMaxDate, maxRangeDays, startParts])

  useEffect(() => {
    if (!isDatePartsComplete(startParts)) return
    setEndParts((prev) => clampDateParts(prev, endBounds.min, endBounds.max))
  }, [endBounds.max, endBounds.min, startParts])

  const validation = useMemo(
    () =>
      validateDateRangeSelection({
        startParts,
        endParts,
        minDate: effectiveMinDate,
        maxDate: effectiveMaxDate,
        maxRangeDays,
      }),
    [effectiveMaxDate, effectiveMinDate, endParts, maxRangeDays, startParts]
  )

  const validationMessage = validation.errorId
    ? intl.formatMessage({id: validation.errorId}, {days: maxRangeDays})
    : null

  const handleStartChange = useCallback(
    (next: DatePartsValue) => {
      setStartParts(clampDateParts(next, startBounds.min, startBounds.max))
    },
    [startBounds.max, startBounds.min]
  )

  const handleEndChange = useCallback(
    (next: DatePartsValue) => {
      setEndParts(clampDateParts(next, endBounds.min, endBounds.max))
    },
    [endBounds.max, endBounds.min]
  )

  const handleApply = useCallback(() => {
    if (!validation.isValid || !validation.start || !validation.end) return
    onDateRangeSelect(validation.start, validation.end)
  }, [onDateRangeSelect, validation])

  const isDark = theme === 'dark'
  const borderColor = isDark ? '#4b5563' : '#e5e7eb'
  const textColor = isDark ? '#ffffff' : '#212529'
  const mutedColor = isDark ? '#9ca3af' : '#6c757d'
  const inputBg = isDark ? '#374151' : '#fff'

  const selectStyle: React.CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 52,
    padding: '4px 2px',
    fontSize: '11px',
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    backgroundColor: inputBg,
    color: textColor,
    cursor: 'pointer',
  }

  const yearSelectStyle: React.CSSProperties = {
    ...selectStyle,
    maxWidth: 58,
  }

  const optionStyle: React.CSSProperties = {
    backgroundColor: inputBg,
    color: textColor,
  }

  const startLabel = intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_START'})
  const endLabel = intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_END'})

  return (
    <div
      className='chatbot-date-range-input'
      title={intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_INPUT_HINT'})}
      style={{
        marginTop: 6,
        marginBottom: 8,
        padding: '8px 10px',
        borderRadius: 6,
        border: `1px solid ${borderColor}`,
        backgroundColor: isDark ? '#1f2937' : '#fff',
        maxWidth: '100%',
      }}
    >
      <div className='d-flex align-items-center gap-2 flex-wrap' style={{rowGap: 6}}>
        <CompactDateRow
          label={startLabel}
          labelId='CHATBOT.ATTENDANCE.DATE_RANGE_START'
          parts={startParts}
          onChange={handleStartChange}
          minBound={startBounds.min}
          maxBound={startBounds.max}
          selectStyle={selectStyle}
          yearSelectStyle={yearSelectStyle}
          optionStyle={optionStyle}
        />

        <span style={{color: mutedColor, fontSize: '11px', flexShrink: 0}} aria-hidden>
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_TO'})}
        </span>

        <CompactDateRow
          label={endLabel}
          labelId='CHATBOT.ATTENDANCE.DATE_RANGE_END'
          parts={endParts}
          onChange={handleEndChange}
          minBound={endBounds.min}
          maxBound={endBounds.max}
          selectStyle={selectStyle}
          yearSelectStyle={yearSelectStyle}
          optionStyle={optionStyle}
        />

        <button
          type='button'
          className='btn btn-sm btn-primary flex-shrink-0'
          onClick={handleApply}
          disabled={!validation.isValid}
          style={{fontSize: '11px', padding: '4px 10px', marginLeft: 'auto'}}
        >
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DATE_RANGE_APPLY'})}
        </button>
      </div>

      {validationMessage && validation.isComplete ? (
        <div style={{fontSize: '10px', color: '#dc3545', marginTop: 6}} role='alert'>
          {validationMessage}
        </div>
      ) : null}
    </div>
  )
}

export default ChatbotDateRangeInput
